/**
 * The step boxes at the top of the Collection, Credit and Replacement tabs.
 *
 * QUALITY-NCR-REVIEW T8 (Aaron's end-to-end test, 30 Sep 2026): Collection had
 * boxed steps naming who acts; Credit and Replacement each drew their own icon
 * stepper with no owner. One component now draws all three, so every process
 * reads the same way: each step's name, and Done / "<who> to act" / who owns it.
 */
import type { LucideIcon } from 'lucide-react';
import { CalendarClock, ClipboardList, Truck, PackageCheck, FileText, BadgeCheck, RotateCcw } from 'lucide-react';

export interface ProcessStep {
  key: string;
  label: string;
  owner: 'Supplier' | 'Customer';
  icon: LucideIcon;
}

interface ProcessStepperProps {
  steps: readonly ProcessStep[];
  /** Index of the step now owed; `steps.length` once the process is complete. */
  current: number;
  /** Set when the other side sent the current step back (credit note or despatch rejected). */
  returned?: boolean;
}

/**
 * @param steps - the process's hand-offs, in order
 * @param current - index of the step owed now (`steps.length` = complete)
 * @param returned - the current step was sent back for rework
 */
export function ProcessStepper({ steps, current, returned = false }: ProcessStepperProps) {
  return (
    <ol className="grid gap-2" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
      {steps.map((step, idx) => {
        const Icon = returned && idx === current ? RotateCcw : step.icon;
        const done = idx < current;
        const active = idx === current;
        return (
          <li
            key={step.key}
            aria-current={active ? 'step' : undefined}
            className={`rounded-md border px-3 py-2 ${
              done ? 'border-emerald-200 bg-emerald-50/60'
                : active ? (step.owner === 'Supplier' ? 'border-amber-300 bg-amber-50/60' : 'border-sky-300 bg-sky-50/60')
                  : 'border-gray-200'
            }`}
          >
            <div className="flex items-center gap-1.5 text-sm font-medium text-[#3f4254]">
              <Icon className={`h-4 w-4 ${done ? 'text-emerald-600' : 'text-muted-foreground'}`} />
              {step.label}
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {done ? 'Done' : active ? `${step.owner} to act${returned ? ' (sent back)' : ''}` : step.owner}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// The customer initiates collection and credit (restored 19 Oct 2026; the spec
// flow had dropped that step and let the supplier start both).
export const COLLECTION_STEPS: readonly ProcessStep[] = [
  { key: 'request', label: 'Collection requested', owner: 'Customer', icon: ClipboardList },
  { key: 'confirm', label: 'Date confirmed', owner: 'Supplier', icon: CalendarClock },
  { key: 'collected', label: 'Goods collected', owner: 'Customer', icon: Truck },
  { key: 'received', label: 'Receipt confirmed', owner: 'Supplier', icon: PackageCheck },
];

/** Collection status -> step owed (4 = complete). An NCR whose legacy
 *  `collection_completed` flag is set is complete whatever its process row says. */
export function collectionStep(status: string | undefined, completed = false): number {
  if (completed) return 4;
  switch (status) {
    case 'pending':
    case 'date_proposed': return 1; // CR-8720: still agreeing the date
    case 'confirmed': return 2;
    case 'collected': return 3;
    case 'completed': return 4;
    default: return 0; // no process / requested (assigned only): the customer initiates
  }
}

export const CREDIT_STEPS: readonly ProcessStep[] = [
  { key: 'request', label: 'Credit requested', owner: 'Customer', icon: ClipboardList },
  { key: 'note', label: 'Credit note entered', owner: 'Supplier', icon: FileText },
  { key: 'received', label: 'Credit received', owner: 'Customer', icon: BadgeCheck },
];

/** Credit status -> step owed (3 = complete). A rejected note goes back to the supplier. */
export function creditStep(status: string | undefined): number {
  switch (status) {
    case 'requested':
    case 'rejected': return 1;
    case 'note_submitted':
    case 'under_review': return 2;
    case 'approved':
    case 'processed': return 3;
    default: return 0; // no process: the customer initiates
  }
}

// CR-8719: the customer's request is the first step, as on Collection and Credit
// (the customer initiates a replacement too; the tab used to start at despatch).
export const REPLACEMENT_STEPS: readonly ProcessStep[] = [
  { key: 'request', label: 'Replacement requested', owner: 'Customer', icon: ClipboardList },
  { key: 'despatch', label: 'Replacement despatched', owner: 'Supplier', icon: Truck },
  { key: 'received', label: 'Receipt confirmed', owner: 'Customer', icon: PackageCheck },
];

/** Replacement status -> step owed (3 = complete). A rejected despatch goes back to the supplier. */
export function replacementStep(status: string | undefined): number {
  switch (status) {
    case 'requested':
    case 'rejected': return 1;
    case 'dispatched': return 2;
    case 'approved':
    case 'completed': return 3;
    default: return 0; // no process: the customer initiates
  }
}
